import queryString from "query-string";
import type {
  CustomerEmployee,
  CardTransaction,
  PaginatedResource,
  UnflownAirway,
} from "~/application/types";
import { DateUtils } from "~/application/utils";
import { api } from "~/infrastructure/api";
import {
  AuthDTO,
  CustomerEmployeeDTO,
  CardTransactionDTO,
  UnflownAirwayDTO,
} from "~/infrastructure/api/dtos";
import {
  mapCustomerEmployeeDTO,
  mapCardTransactionDTO,
  mapUserDTO,
} from "~/infrastructure/api/mappers";
import { EditableCustomerEmployee } from "~/presentation/shared/views/CustomerEmployeeDialog";
import { DateFormats, displayDate, toDate } from "~/utils/date.utils";
import { convertKeysToSnakeCase } from "~/utils/object.utils";
import type {
  ChangeCustomerEmployeePasswordProps,
  ChangePasswordResult,
  GroupOptionsType,
  ICustomerEmployeeService,
  IFindCustomerEmployeeParams,
  IFindTransactionsParams,
} from "./ICustomerEmployeeService";
import { mapUnflownAirway } from "~/infrastructure/api/mappers/mapUnflownAirway";
import { FormDataBuilder } from "~/application/utils/object-functions";
import { getAuthToken } from "~/utils/auth.utils";
import { downloadBlob } from "~/utils";

export type GroupsDTO = {
  description: string;
  uuid: string;
  customer_uuid: string;
  active: boolean;
};

export class CustomerEmployeeService implements ICustomerEmployeeService {
  async findById(id: string): Promise<CustomerEmployee> {
    return await api
      .get<CustomerEmployeeDTO>(`/customers/customer-employees/${id}`)
      .then(({ data }) => mapCustomerEmployeeDTO(data));
  }

  async find({
    page = 1,
    name,
    isActive,
    approver = null,
    customerId,
    profile,
    onlyUser,
    headquartersAccess,
    branchUuids,
    isVip = null,
  }: IFindCustomerEmployeeParams): Promise<PaginatedResource<CustomerEmployee[]>> {
    const url = queryString.stringifyUrl(
      {
        url: `/customers/${customerId}/customer-employees`,
        query: {
          page,
          name,
          profile,
          is_active: isActive,
          only_user: onlyUser,
          approver: approver === true ? true : undefined,
          headquarters_access: headquartersAccess === true ? true : undefined,
          branch_uuids: headquartersAccess === true ? undefined : branchUuids,
          is_vip: isVip,
        },
      },
      { arrayFormat: "bracket" },
    );

    return await api
      .get<PaginatedResource<CustomerEmployeeDTO[]>>(url)
      .then(({ data: { data, links, meta, filters } }) => ({
        links: links,
        meta: meta,
        filters: filters,
        data: data.map(mapCustomerEmployeeDTO),
      }));
  }

  async create(data: EditableCustomerEmployee): Promise<CustomerEmployee> {
    if (data.birthDate) {
      data.birthDate = toDate(data.birthDate, DateFormats.SMALL_DATE).toISOString();
    }

    if (data.passportExpiration) {
      data.passportExpiration = toDate(
        data.passportExpiration,
        DateFormats.SMALL_DATE,
      ).toISOString();
    }

    const associations = {
      has_headquarters_access: data.associations?.has_headquarters_access ?? false,
      branch_uuids: data.associations.mark_all_branches ? [] : data.associations.branch_uuids,
      mark_all_branches: data.associations?.mark_all_branches ?? false,
      cost_center_uuids: (data.costCenters ?? []).map((costCenter) => costCenter.uuid),
    };

    return await api
      .post<CustomerEmployeeDTO>(`/customers/${data.customerId}/customer-employees`, {
        name: data.name,
        last_name: data.lastName,
        phone: data.phone,
        email: data.email,
        gender: data.gender,
        cpf: data.cpf,
        rg: data.rg,
        birthdate: data.birthDate,
        registration: data.registration,
        group_uuid: data.group.uuid,
        position: data.position,
        outsourced: data.outsourced,
        passport_number: data.passportNumber,
        passport_expiration: data.passportExpiration,
        is_vip: data.isVip,
        loyalty_latam: data.loyaltyLatam,
        loyalty_azul: data.loyaltyAzul,
        loyalty_gol: data.loyaltyGol,
        preferred_seat: data.preferredSeat,
        road_location: data.roadLocation,
        type_air_seat: data.typeAirSeat,
        customer_uuid: data.customerId,
        profile_uuid: data.profile?.uuid,
        policy_uuid: data.policyId,
        create_user: data.canCreateUser ?? false,
        associations: associations,
      })
      .then(({ data }) => mapCustomerEmployeeDTO(data));
  }

  async findGroups({
    customerId,
    description,
  }: {
    customerId: string;
    description: string;
  }): Promise<GroupOptionsType[]> {
    const url = queryString.stringifyUrl({
      url: `/customers/${customerId}/groups`,
      query: {
        description,
      },
    });

    return await api.get(url).then(({ data }) =>
      data.data.map((group: GroupsDTO) => ({
        description: group.description,
        uuid: group.uuid,
        customerId: group.customer_uuid,
        active: group.active,
      })),
    );
  }

  async findTransactions({
    cardUuid,
    page = 1,
    search,
    startDate,
    endDate,
    status,
    minAmount,
    maxAmount,
  }: IFindTransactionsParams): Promise<PaginatedResource<CardTransaction[]>> {
    const url = queryString.stringifyUrl(
      {
        url: `/customer-employees/cards/${cardUuid}/transactions`,
        query: {
          page,
          search,
          start_date: startDate ? displayDate(startDate, DateFormats.ISO_TIME_DATE) : undefined,
          end_date: endDate ? displayDate(endDate, DateFormats.ISO_TIME_DATE) : undefined,
          expense_proof_status: status,
          min_amount: minAmount,
          max_amount: maxAmount,
        },
      },
      {
        arrayFormat: "bracket",
      },
    );

    return await api.get<PaginatedResource<CardTransactionDTO[]>>(url).then(({ data }) => ({
      links: data.links,
      meta: data.meta,
      data: data.data.map(mapCardTransactionDTO),
    }));
  }

  async updateById(data: EditableCustomerEmployee): Promise<CustomerEmployee> {
    if (data.birthDate) {
      data.birthDate = toDate(data.birthDate, DateFormats.SMALL_DATE).toISOString();
    }

    if (data.passportExpiration) {
      data.passportExpiration = toDate(
        data.passportExpiration,
        DateFormats.SMALL_DATE,
      ).toISOString();
    }

    const associations = {
      has_headquarters_access: data.associations?.has_headquarters_access ?? false,
      branch_uuids: Array.isArray(data.associations?.branch_uuids)
        ? data.associations.branch_uuids
        : [],
      mark_all_branches: data.associations?.mark_all_branches ?? false,
      cost_center_uuids: (data.costCenters ?? []).map((costCenter) => costCenter.uuid),
    };

    if (associations.mark_all_branches) {
      associations.branch_uuids = [];
    }

    return await api
      .put<CustomerEmployeeDTO>(`/customers/customer-employees/${data.uuid}`, {
        name: data.name,
        last_name: data.lastName,
        phone: data.phone,
        email: data.email,
        gender: data.gender,
        cpf: data.cpf,
        rg: data.rg,
        group_uuid: data.group?.uuid,
        birthdate: data.birthDate,
        registration: data.registration,
        position: data.position,
        outsourced: data.outsourced,
        passport_number: data.passportNumber,
        passport_expiration: data.passportExpiration,
        is_vip: data.isVip,
        loyalty_latam: data.loyaltyLatam,
        loyalty_azul: data.loyaltyAzul,
        loyalty_gol: data.loyaltyGol,
        preferred_seat: data.preferredSeat,
        road_location: data.roadLocation,
        avatar: data.image,
        avatar_url: data.avatarUrl,
        type_air_seat: data.typeAirSeat,
        customer_uuid: data.customerId,
        profile_uuid: data.profile?.uuid,
        policy_uuid: data.policyId,
        create_user: Boolean(data.canCreateUser ?? false),
        associations: associations,
      })
      .then(({ data }) => mapCustomerEmployeeDTO(data));
  }

  async findAllUnflownsAirways({
    customerEmployeeId,
    status,
  }: {
    customerEmployeeId: string;
    status: string;
  }): Promise<UnflownAirway[]> {
    const url = queryString.stringifyUrl({
      url: "/unflown-airways",
      query: { customer_employee_uuid: customerEmployeeId, status },
    });

    return await api
      .get<PaginatedResource<UnflownAirwayDTO[]>>(url)
      .then(({ data }) => data.data.map(mapUnflownAirway));
  }

  async toggleActive(data: CustomerEmployee): Promise<void> {
    if (data.isActive) {
      return await api
        .patch<void>(`/customers/customer-employees/${data.uuid}/inactive`)
        .then(({ data }) => data);
    }

    return await api
      .patch<void>(`/customers/customer-employees/${data.uuid}/active`)
      .then(({ data }) => data);
  }

  async exportToSpreadsheet(customerId: string): Promise<void> {
    return fetch(`${api.baseUrl}/customers/${customerId}/customer-employees/exports`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    })
      .then((response) => response.blob())
      .then((blob) => downloadBlob(blob, "funcionarios.xlsx"));
  }

  async findCustomer(data: { uuid: string }): Promise<CustomerEmployee> {
    return await api
      .get(`/customers/customer-employees/${data.uuid}`)
      .then(({ data }) => mapCustomerEmployeeDTO(data));
  }

  async updateAvatar({
    uuid,
    file,
  }: {
    uuid: string;
    file: File | null | undefined;
  }): Promise<{ avatarUrl: string }> {
    const formDataBuilder = new FormDataBuilder({
      avatar: file,
    });

    const formData = formDataBuilder.getFormData();

    return await api
      .post(`/users/${uuid}/avatar`, formData, {
        "Content-Type": "multipart/form-data",
      })
      .then(({ data }) => ({ avatarUrl: data.avatar_url || null }));
  }

  async changePassword({
    password,
    confirmedPassword,
    csrfToken,
    secretKey,
  }: ChangeCustomerEmployeePasswordProps): Promise<ChangePasswordResult> {
    const url = "/change-password";
    const data = {
      password,
      confirmedPassword,
      csrfToken,
      secret: secretKey,
    };

    return await api
      .post<{ data: AuthDTO }>(url, convertKeysToSnakeCase(data))
      .then(({ data: response }) => ({
        user: mapUserDTO(response.data.user),
        token: {
          value: response.data.token.value as string,
          createdAt: DateUtils.toDate(response.data.token.created_at),
        },
      }));
  }
}
